import { RefObject, useEffect } from "react";

/** Google mounts suggestion menus on body, outside the listing form. */
export function usePlacesDropdownDismissal(root: RefObject<HTMLDivElement>, step: number) {
  useEffect(() => {
    let dismissed = false;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const sync = () => {
      const active = document.activeElement as HTMLElement | null;
      const inputActive = !!active && root.current?.contains(active) && active.matches("input.pac-target-input") && !!(active as HTMLInputElement).value.trim();
      document.querySelectorAll<HTMLElement>(".pac-container").forEach((menu) => {
        const hide = dismissed || !inputActive || !menu.querySelector(".pac-item");
        if (hide) menu.setAttribute("data-listing-dismissed", "true");
        else menu.removeAttribute("data-listing-dismissed");
      });
    };
    const onFocus = () => { dismissed = false; sync(); };
    const onBlur = () => { dismissed = true; sync(); };
    const onInput = () => {
      dismissed = false;
      clearTimeout(timeout);
      sync();
      // Also dismiss a stale menu if a failed request never updates it.
      timeout = setTimeout(() => { dismissed = true; sync(); }, 5000);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" || event.key === "Tab") { dismissed = true; sync(); }
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".pac-container, input.pac-target-input")) { dismissed = true; sync(); }
    };
    const observer = new MutationObserver((records) => {
      if (records.some((record) => record.type === "childList" &&
        record.target instanceof Element && record.target.closest(".pac-container") &&
        record.addedNodes.length > 0)) {
        clearTimeout(timeout);
      }
      sync();
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["style"] });
    document.addEventListener("focusin", onFocus);
    document.addEventListener("focusout", onBlur);
    document.addEventListener("input", onInput);
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    sync();
    return () => {
      clearTimeout(timeout);
      observer.disconnect();
      document.removeEventListener("focusin", onFocus);
      document.removeEventListener("focusout", onBlur);
      document.removeEventListener("input", onInput);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
      dismissed = true;
      sync();
    };
  }, [root, step]);
}
